document.addEventListener("DOMContentLoaded", () => {
    gsap.registerPlugin(ScrollTrigger);

    // Mobile/Safari hardening
    ScrollTrigger.config({
        ignoreMobileResize: true,
        // keeps things stable when iOS address bar changes height
        autoRefreshEvents: "visibilitychange,DOMContentLoaded,load,resize",
    });

    const safeRefresh = () => {
        // rAF avoids refreshing mid-layout on mobile Safari
        requestAnimationFrame(() => ScrollTrigger.refresh());
    };

    // Refresh on real load, BFCache restore, tab focus, and orientation change
    window.addEventListener("load", () => setTimeout(safeRefresh, 80));

    window.addEventListener("pageshow", (e) => {
        // BFCache restore on iOS Safari
        if (e.persisted) setTimeout(safeRefresh, 80);
        else setTimeout(safeRefresh, 80);
    });

    document.addEventListener("visibilitychange", () => {
        if (!document.hidden) setTimeout(safeRefresh, 80);
    });

    window.addEventListener("orientationchange", () => {
        setTimeout(safeRefresh, 250);
    });

    // If supported, wait for fonts too (helps layout shifts)
    if (document.fonts?.ready) {
        document.fonts.ready.then(() => setTimeout(safeRefresh, 80));
    }

    const mm = gsap.matchMedia();
    const toArray = gsap.utils.toArray;

    // Helpers (create one ScrollTrigger per element for reliability)
    const reveal = (selectorOrEl, tweenVars = {}, stVars = {}) => {
        const els =
            typeof selectorOrEl === "string" ? toArray(selectorOrEl) : [selectorOrEl];
        els.forEach((el) => {
            if (!el) return;

            gsap.set(el, { willChange: "transform,opacity" });

            gsap.from(el, {
                autoAlpha: 0,
                ...tweenVars,
                scrollTrigger: {
                    trigger: el,
                    invalidateOnRefresh: true,
                    ...stVars,
                },
            });
        });
    };

    // Batch for lots of cards (lighter + more consistent on mobile)
    const batchReveal = (selector, vars = {}, stVars = {}) => {
        const els = toArray(selector);
        if (!els.length) return;

        ScrollTrigger.batch(els, {
            start: "top 92%",
            onEnter: (batch) =>
                gsap.from(batch, { autoAlpha: 0, ...vars, stagger: 0.12 }),
            onEnterBack: (batch) =>
                gsap.to(batch, {
                    autoAlpha: 1,
                    ...vars,
                    stagger: 0.08,
                    duration: 0.25,
                }),
            ...stVars,
        });
    };

    mm.add(
        {
            reduce: "(prefers-reduced-motion: reduce)",
            mobile: "(max-width: 1023px)",
            desktop: "(min-width: 1024px)",
        },
        (context) => {
            const { reduce, mobile, desktop } = context.conditions;

            // IMPORTANT: make reduced-motion exclusive (prevents double-animations)
            if (reduce) {
                const stOnce = { start: "top 90%", once: true };

                reveal(
                    ".mateIntroSectionLeft",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(".mateDrapes h1", { duration: 0.6, ease: "power2.out" }, stOnce);
                reveal(".mateDrapes p", { duration: 0.6, ease: "power2.out" }, stOnce);
                reveal(
                    ".mateDrapes .category-slider",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(
                    ".whyMateIntro span",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(
                    ".whyMateIntro h1",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );

                batchReveal(
                    ".whyMateCard",
                    { y: 18, duration: 0.5, ease: "power2.out" },
                    { once: true }
                );

                reveal(
                    ".bespokeServicesLeft .bespokeTag",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(
                    ".bespokeServicesLeft h1",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(
                    ".bespokeServicesLeft p",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                reveal(
                    ".bespokeServicesRight img",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );

                reveal(
                    ".whyBespokeServicesContent h1",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                batchReveal(
                    ".whyBespokeCard",
                    { y: 18, duration: 0.5, ease: "power2.out" },
                    { once: true }
                );

                reveal(
                    ".fourStepRitual > h1",
                    { duration: 0.6, ease: "power2.out" },
                    stOnce
                );
                batchReveal(
                    ".fourStepRitualStep",
                    { y: 18, duration: 0.5, ease: "power2.out" },
                    { once: true }
                );

                reveal(
                    ".btnBookConsultation",
                    { duration: 0.6, ease: "power2.out" },
                    { start: "top 95%", once: true }
                );

                // One final refresh after everything is created in this mode
                setTimeout(safeRefresh, 60);
                return;
            }

            if (mobile) {
                // Mobile: avoid reverse-hiding, keep it stable
                const stOnce = { start: "top 92%", once: true };

                // --- MateIntro Right Column (images) ---
                const leftColumn = toArray(
                    ".mateIntroSectionRight > .flex:first-child img"
                );
                const rightColumn = toArray(
                    ".mateIntroSectionRight > .flex:last-child img"
                );

                if (leftColumn.length) {
                    gsap.from(leftColumn, {
                        y: 60,
                        autoAlpha: 0,
                        stagger: 0.15,
                        duration: 0.8,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".mateIntroSectionRight",
                            start: "top 92%",
                            once: true,
                            invalidateOnRefresh: true,
                        },
                    });
                }

                if (rightColumn.length) {
                    gsap.from(rightColumn, {
                        y: -60,
                        autoAlpha: 0,
                        stagger: 0.15,
                        duration: 0.8,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".mateIntroSectionRight",
                            start: "top 92%",
                            once: true,
                            invalidateOnRefresh: true,
                        },
                    });
                }

                // --- MateIntro Left Section ---
                reveal(
                    ".mateIntroSectionLeft",
                    { y: 70, duration: 0.8, ease: "power3.out" },
                    { ...stOnce, start: "top 92%" }
                );

                // --- MateDrapes Section ---
                reveal(
                    ".mateDrapes h1",
                    { y: 60, duration: 0.8, ease: "power3.out" },
                    stOnce
                );
                reveal(
                    ".mateDrapes p",
                    { y: 70, duration: 0.8, ease: "power3.out" },
                    { ...stOnce, start: "top 92%" }
                );
                reveal(
                    ".mateDrapes .category-slider",
                    { y: 80, duration: 0.8, ease: "power3.out" },
                    stOnce
                );

                // --- Video BG Parallax (gentle) ---
                gsap.to(".videoBG", {
                    y: -80,
                    ease: "none",
                    scrollTrigger: {
                        trigger: ".videoBG",
                        start: "top bottom",
                        end: "bottom top",
                        scrub: 0.3,
                        invalidateOnRefresh: true,
                    },
                });

                // --- Why Mate Intro ---
                reveal(
                    ".whyMateIntro span",
                    { y: 40, duration: 0.7, ease: "power3.out" },
                    stOnce
                );
                reveal(
                    ".whyMateIntro h1",
                    { y: 60, duration: 0.8, ease: "power3.out" },
                    stOnce
                );

                // --- Why Mate Cards (batch, reliable) ---
                batchReveal(
                    ".whyMateCard",
                    { y: 40, duration: 0.7, ease: "power3.out" },
                    { once: true }
                );

                // --- Bespoke Services ---
                reveal(
                    ".bespokeServicesLeft .bespokeTag",
                    { y: 35, duration: 0.7, ease: "power3.out" },
                    stOnce
                );
                reveal(
                    ".bespokeServicesLeft h1",
                    { y: 55, duration: 0.8, ease: "power3.out" },
                    stOnce
                );
                reveal(
                    ".bespokeServicesLeft p",
                    { y: 65, duration: 0.8, ease: "power3.out" },
                    stOnce
                );

                reveal(
                    ".bespokeServicesRight img",
                    {
                        x: 80,
                        scale: 1.05,
                        duration: 0.8,
                        ease: "power3.out",
                    },
                    { ...stOnce, trigger: ".bespokeServicesRight", start: "top 92%" }
                );

                // --- Why Bespoke ---
                reveal(
                    ".whyBespokeServicesContent h1",
                    { y: 55, duration: 0.8, ease: "power3.out" },
                    stOnce
                );
                batchReveal(
                    ".whyBespokeCard",
                    { y: 40, duration: 0.7, ease: "power3.out" },
                    { once: true }
                );

                // --- Four Step Ritual ---
                reveal(
                    ".fourStepRitual > h1",
                    { y: 55, duration: 0.8, ease: "power3.out" },
                    stOnce
                );
                batchReveal(
                    ".fourStepRitualStep",
                    { y: 40, duration: 0.7, ease: "power3.out" },
                    { once: true }
                );

                // --- CTA Button ---
                reveal(
                    ".btnBookConsultation",
                    { y: 60, duration: 0.8, ease: "power3.out" },
                    { start: "top 95%", once: true }
                );

                // Extra refresh shortly after init (helps on iOS)
                setTimeout(safeRefresh, 120);
                return;
            }

            if (desktop) {
                // Your desktop behavior, mostly same, just autoAlpha + invalidate + refresh resiliency
                const leftColumn = toArray(
                    ".mateIntroSectionRight > .flex:first-child img"
                );
                const rightColumn = toArray(
                    ".mateIntroSectionRight > .flex:last-child img"
                );

                if (leftColumn.length) {
                    gsap.from(leftColumn, {
                        y: 100,
                        autoAlpha: 0,
                        stagger: 0.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".mateIntroSectionRight",
                            start: "top 80%",
                            end: "bottom 50%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });
                }

                if (rightColumn.length) {
                    gsap.from(rightColumn, {
                        y: -100,
                        autoAlpha: 0,
                        stagger: 0.2,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: ".mateIntroSectionRight",
                            start: "top 80%",
                            end: "bottom 50%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });
                }

                gsap.from(".mateIntroSectionLeft", {
                    y: 120,
                    autoAlpha: 0,
                    ease: "power3.out",
                    duration: 1.2,
                    scrollTrigger: {
                        trigger: ".mateIntroSectionLeft",
                        start: "top 85%",
                        end: "top 50%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".mateDrapes h1", {
                    y: 100,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".mateDrapes",
                        start: "top 80%",
                        end: "top 50%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".mateDrapes p", {
                    y: 120,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".mateDrapes",
                        start: "top 75%",
                        end: "top 45%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".mateDrapes .category-slider", {
                    y: 150,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".mateDrapes .category-slider",
                        start: "top 85%",
                        end: "top 55%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.to(".videoBG", {
                    y: -300,
                    ease: "none",
                    scrollTrigger: {
                        trigger: ".videoBG",
                        start: "top bottom",
                        end: "bottom top",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".whyMateIntro span", {
                    y: -50,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".whyMateIntro",
                        start: "top 85%",
                        end: "top 65%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".whyMateIntro h1", {
                    y: 120,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".whyMateIntro",
                        start: "top 80%",
                        end: "top 50%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                toArray(".whyMateCard").forEach((card, index) => {
                    const direction = index % 2 === 0 ? 100 : -100;
                    gsap.from(card, {
                        y: direction,
                        autoAlpha: 0,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 85%",
                            end: "top 60%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });
                });

                gsap.from(".bespokeServicesLeft .bespokeTag", {
                    y: 50,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".bespokeServicesContent",
                        start: "top 85%",
                        end: "top 60%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".bespokeServicesLeft h1", {
                    y: 80,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".bespokeServicesContent",
                        start: "top 80%",
                        end: "top 55%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".bespokeServicesLeft p", {
                    y: 100,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".bespokeServicesContent",
                        start: "top 75%",
                        end: "top 50%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".bespokeServicesRight img", {
                    x: 150,
                    scale: 1.1,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".bespokeServicesRight",
                        start: "top 85%",
                        end: "top 55%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                gsap.from(".whyBespokeServicesContent h1", {
                    y: 80,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".whyBespokeServicesContent",
                        start: "top 85%",
                        end: "top 60%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                toArray(".whyBespokeCard").forEach((card, index) => {
                    const direction = index % 2 === 0 ? 100 : -100;
                    gsap.from(card, {
                        y: direction,
                        autoAlpha: 0,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 90%",
                            end: "top 60%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });
                });

                gsap.from(".fourStepRitual > h1", {
                    y: 80,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".fourStepRitual",
                        start: "top 85%",
                        end: "top 60%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                const cornerAnimations = [
                    { x: -120, y: -120 },
                    { x: 120, y: -120 },
                    { x: -120, y: 120 },
                    { x: 120, y: 120 },
                ];

                toArray(".fourStepRitualStep").forEach((card, i) => {
                    gsap.from(card, {
                        x: cornerAnimations[i]?.x ?? 0,
                        y: cornerAnimations[i]?.y ?? 0,
                        autoAlpha: 0,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: card,
                            start: "top 90%",
                            end: "top 60%",
                            scrub: true,
                            invalidateOnRefresh: true,
                        },
                    });
                });

                // NOTE: your original had opacity: 1 in "from" (likely a typo)
                gsap.from(".btnBookConsultation", {
                    y: 100,
                    autoAlpha: 0,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: ".btnBookConsultation",
                        start: "top 90%",
                        end: "top 65%",
                        scrub: true,
                        invalidateOnRefresh: true,
                    },
                });

                setTimeout(safeRefresh, 60);
                return;
            }
        }
    );
});
